import React, { ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { SibTableContainer, Table, useTableSettings } from '@sib/ui';
import { useElementSize } from '@sib/ui/hooks';
import { DEFAULT_PAGE_SIZE } from '../constants';
import {
  useColumnResizeVariables,
  useGetSelectedRowOriginalData,
} from '../hooks';
import {
  MemoizedSibTableBody,
  SibTableNoResultsRow,
  SibTableNoResultsRowProps,
} from './body';
import {
  SibTableFooter,
  SibTablePagination,
  SibTablePaginationProps,
} from './footer';
import { SibTableHeader, SibTableSettings } from './header';

interface SibDataTableProps<T> {
  rowCount?: number;
  data: T[];
  columns: ColumnDef<T, unknown>[];
  settings: ReturnType<typeof useTableSettings<T>>;
  minPage?: number;
  loading?: boolean;
  withFixedColumnWidth?: boolean;
  withSettingsRow?: boolean;
  preserveSelectedValues?: boolean;
  withDelayedLoading?: boolean;
  hidePagination?: boolean;
  hideFooter?: boolean;
  noResultsPlaceholder?: ReactNode;
  NoResultsContainerProps?: Partial<SibTableNoResultsRowProps>;
  TableContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  TablePaginationProps?: Partial<SibTablePaginationProps>;
  TableProps?: React.HTMLAttributes<HTMLTableElement>;
  withFilters?: boolean;
  onRowClick?: (row: T) => void;
}

type WithSubRows<T> = T & { subRows?: T[] };

export const SibDataTable = <T,>({
  settings: {
    tableProps,
    pagination,
    onPaginationChange,
    minPage,
    onOriginalRowSelectionChange,
    disabledColumns,
  },
  rowCount,
  data,
  columns,
  noResultsPlaceholder,
  hideFooter = true,
  hidePagination = false,
  withFixedColumnWidth = false,
  loading = false,
  withSettingsRow = false,
  preserveSelectedValues = false,
  withDelayedLoading = false,
  NoResultsContainerProps,
  TableProps,
  TableContainerProps,
  TablePaginationProps,
  withFilters,
  onRowClick,
}: SibDataTableProps<T>) => {
  const showNoResults = !loading && data.length === 0;
  const { width, ref } = useElementSize<HTMLTableElement>({
    enabled: showNoResults || (loading && withDelayedLoading),
  });

  const getSelectedRowOriginalData = useGetSelectedRowOriginalData({ data });
  const table = useReactTable({
    data,
    columns,
    rowCount,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableExpanding: true,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => (row as WithSubRows<T>).subRows ?? [],
    manualFiltering: true,
    ...tableProps,
    onRowSelectionChange: updater => {
      tableProps?.onRowSelectionChange?.(updater);
      if (!preserveSelectedValues) return;

      const valuesToCompare =
        typeof updater === 'function'
          ? updater(tableProps.state?.rowSelection || {})
          : updater;

      const originalData = getSelectedRowOriginalData(
        updater,
        valuesToCompare,
        tableProps.state?.originalRowSelection as T[]
      );

      onOriginalRowSelectionChange?.(originalData);
    },
  });

  const columnSizeVars = useColumnResizeVariables({ table });

  const tableState = table.getState();

  return (
    <SibTableContainer {...TableContainerProps} ref={ref}>
      {withSettingsRow && (
        <SibTableSettings table={table} disabledColumns={disabledColumns} />
      )}
      <Table
        {...TableProps}
        className="w-max min-w-full border-collapse"
        style={{
          ...(withFixedColumnWidth && {
            tableLayout: 'fixed',
          }),
          ...TableProps?.style,
          ...columnSizeVars,
        }}
        disableWrapper
      >
        <SibTableHeader
          table={table}
          headerGroups={table.getHeaderGroups()}
          hideRounding={withSettingsRow}
          withFilters={withFilters}
        />
        <MemoizedSibTableBody
          TableDelayedLoadingIndicatorProps={{
            width,
          }}
          withDelayedLoading={withDelayedLoading}
          loading={loading}
          isEmpty={showNoResults}
          table={table}
          pageSize={pagination.pageSize || DEFAULT_PAGE_SIZE}
          rowSelection={tableState.rowSelection}
          columnPinning={tableState.columnPinning}
          onRowClick={onRowClick}
          emptyPlaceholder={
            <SibTableNoResultsRow
              colSpan={table.getAllColumns().length}
              {...NoResultsContainerProps}
              width={width}
            >
              {noResultsPlaceholder}
            </SibTableNoResultsRow>
          }
        />
        {!hideFooter && <SibTableFooter table={table} />}
      </Table>
      {data.length > 0 && typeof rowCount === 'number' && !hidePagination && (
        <SibTablePagination
          onChange={onPaginationChange}
          rowCount={rowCount}
          state={pagination}
          minPage={minPage}
          {...TablePaginationProps}
        />
      )}
    </SibTableContainer>
  );
};
